import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { SectionBase } from './SectionBase';
import styled from 'styled-components';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import QuestionsText from './ImpactSummary/QuestionsText';
import { getHelpText } from './ImpactSummary/HelperComponents';
import IndicatorStages from './ImpactSummaryTable/IndicatorStages';
import DateSection from './DateSection';
import { parseIndicatorSummary } from './SectionPeriod/utils';
import ExampleText from './ImpactSummary/ExampleText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { capitalizeOnlyFirstLetter } from 'shared/utils/stringUtils';
import { useIndicator } from 'shared/providers/IndicatorProvider';

export const IMPACT_SUMMARY_QUESTION_ID = 40015;
export const CAPACITY_DEVELOPMENT_QUESTION_ID = 40021;
export const SERVICE_GOAL_QUESTION_ID = 40003;
export const BENEFICIARY_TERM_QUESTION_ID = 40004;
export const INTERMEDIARIES_QUESTION_ID = 40022;
export const SERVICE_TYPE_QUESTION_ID = 40006;
export const SOCIAL_IMPACT_QUESTION_ID = 40010;
export const REACH_QUESTION_ID = 40011;
export const PROGRAM_OUTCOME_QUESTION_ID = 40005;

const marginBottom = { marginBottom: SPACERS.tiny };
export const directRegex = /To .+ themselves/;
export const indirectRegex = /To family members, caregivers, volunteers, or other intermediaries, who in turn serve .+/;
export const getBeneficiaryQuestionText = (beneficiaryTerm, index) => {
  if (index === 0) {
    return `To ${beneficiaryTerm} themselves`;
  }
  return `To family members, caregivers, volunteers, or other intermediaries, who in turn serve ${beneficiaryTerm}`;
};
export const renderDirectBeneficiaryText = text => (
  <>
    <div style={marginBottom} className="beneficiary-model-text">
      <strong>Directly</strong>
    </div>
    {capitalizeOnlyFirstLetter(text)}
  </>
);
export const renderIndirectBeneficiaryText = text => (
  <>
    <div style={marginBottom} className="beneficiary-model-text">
      <strong>Indirectly</strong>
    </div>
    {capitalizeOnlyFirstLetter(text)}
  </>
);

const getLabel = beneficiaryTerm => ({
  40005: val => {
    const split = val.split('. ');
    const optionLabel = split[0];
    const beneficiaryTermLowerCase =
      beneficiaryTerm?.toLowerCase() || 'beneficiaries';
    const optionText =
      optionLabel === 'Direct service'
        ? `Providing goods or service directly to ${beneficiaryTermLowerCase} or their families or caregivers.`
        : optionLabel === 'Capacity development'
        ? `Strengthening other organizations or programs that serve ${beneficiaryTermLowerCase}.`
        : `Changing laws of policies to benefit ${beneficiaryTermLowerCase}.`;
    return (
      <>
        <div style={marginBottom}>
          <strong>{optionLabel}</strong>
        </div>
        {optionText}
      </>
    );
  },
  40006: val => {
    if (directRegex.test(val)) {
      return renderDirectBeneficiaryText(val);
    } else if (indirectRegex.test(val)) {
      return renderIndirectBeneficiaryText(val);
    }
    return val;
  },
});

const defaultGetLabel = val => val;
const capacityDevelopmentRegEx = /^Capacity development/i;
export const SectionMultiRadio = ({
  isDebug,
  section,
  title,
  subTitle,
  helpText,
  options: options0,
  questionId,
  value,
  values,
  onChange,
  reportDates,
  setReportBuilderValues,
  error,
  intermediaries,
  programName,
  extraData,
  extraClass = '',
  prevQuestion,
  sectAnswers,
  handleNext,
  handleBack,
  userAnswerId,
  reloadAnswers,
  beneficiaryTerm,
  isReportPassed,
  guidanceText,
  isImpactSummaryCompleted,
}) => {
  const { intermediariesQuestionId, setLearnAndAct } = useIndicator();
  intermediaries = intermediaries || values[intermediariesQuestionId];
  const options = useMemo(() => options0, [questionId, options0]);

  const helperText = useMemo(
    () =>
      getHelpText(
        questionId,
        sectAnswers,
        helpText,
        beneficiaryTerm,
        intermediaries,
      ),
    [helpText, questionId, sectAnswers, beneficiaryTerm],
  );
  const [reloadAndShow, setReloadAndShow] = useState(true);
  const handleChange = useCallback(
    value => {
      setLearnAndAct(questionId, value);
      onChange(questionId, value);
    },
    [questionId, onChange, setLearnAndAct],
  );
  useEffect(() => {
    if (!value) {
      setReloadAndShow(false);
      setTimeout(() => {
        setReloadAndShow(true);
      }, 0);
    }
  }, [value]);
  const hasSummaryTable = IMPACT_SUMMARY_QUESTION_ID === questionId;
  const pageSectionProps = {
    title,
    subTitle: <QuestionsText questionId={questionId} defaultTitle={subTitle} />,
    helpText: helperText,
    error,
    extraClass,
    questionId,
    guidanceText,
  };
  const pageOptionProps = {
    variant: 'radio',
    options: options,
    getSelected: val => {
      if (questionId === SERVICE_TYPE_QUESTION_ID) {
        return (
          value === val ||
          (directRegex.test(value) && directRegex.test(val)) ||
          (indirectRegex.test(value) && indirectRegex.test(val))
        );
      }
      return value === val;
    },
    getLabel: getLabel(beneficiaryTerm)[questionId] || defaultGetLabel,
    onClick: handleChange,
  };

  if (section === 'Dates') {
    return (
      <SectionBase {...pageSectionProps}>
        {reloadAndShow && (
          <DateSection
            questionId={questionId}
            pageOptionProps={pageOptionProps}
          />
        )}
      </SectionBase>
    );
  }

  if (hasSummaryTable) {
    const oldValues = parseIndicatorSummary(values[IMPACT_SUMMARY_QUESTION_ID]);
    const programOutcome = values[PROGRAM_OUTCOME_QUESTION_ID];
    const serviceGoal = values[SERVICE_GOAL_QUESTION_ID];
    const serviceType = values[SERVICE_TYPE_QUESTION_ID];
    const selectedReachIndicators = parseIndicatorSummary(
      values[REACH_QUESTION_ID],
    );
    const selectedSocialImpactIndicators = parseIndicatorSummary(
      values[SOCIAL_IMPACT_QUESTION_ID],
    );
    const selectedCapacityIndicators = parseIndicatorSummary(
      values[CAPACITY_DEVELOPMENT_QUESTION_ID],
    );
    const service =
      extraData?.service ||
      extraData?.services?.find(s => s.name === serviceGoal);
    return (
      <IndicatorStages
        isDebug={isDebug}
        handleNext={handleNext}
        handleBack={handleBack}
        questionId={questionId}
        title={title}
        error={error}
        beneficiaryTerm={beneficiaryTerm}
        programName={programName}
        intermediaries={intermediaries}
        serviceGoal={serviceGoal}
        serviceType={serviceType}
        helpText={helpText}
        reloadAnswers={reloadAnswers}
        handleChange={handleChange}
        setReportBuilderValues={setReportBuilderValues}
        service={service}
        reportId={extraData?.reportId}
        services={extraData?.services}
        indicators={extraData?.indicators}
        isReportPassed={isReportPassed}
        reportDates={reportDates}
        capacityDevelopmentEnabled={capacityDevelopmentRegEx.test(
          programOutcome,
        )}
        learnEnabled={extraData?.learnEnabled}
        actEnabled={extraData?.actEnabled}
        prevQuestion={prevQuestion}
        selectedCapacityIndicators={selectedCapacityIndicators}
        selectedSocialImpactIndicators={selectedSocialImpactIndicators}
        selectedReachIndicators={selectedReachIndicators}
        pageSectionProps={pageSectionProps}
        pageOptionProps={pageOptionProps}
        indicatorSummary={oldValues}
        userAnswerId={userAnswerId}
        isImpactSummaryCompleted={isImpactSummaryCompleted}
        sectionAnswers={sectAnswers}
      />
    );
  }

  return (
    <SectionBase {...pageSectionProps}>
      {reloadAndShow && <SelectorOptions {...pageOptionProps} />}
      <ExampleText questionId={questionId} />
    </SectionBase>
  );
};

const Box = styled.div`
  width: 100%;
  height: auto;
  margin-top: 20px;
  min-height: 40px;
  padding-left: 15px;
  padding-right: 15px;
  border: solid 1px #cbd6e2;
  border-radius: 4px;
  background-color: #ffffff;
`;
